/**
 * audit-action-meta — 操作日志「动作」展示映射（纯前端，契约 AdminAuditLogResponse 只回 raw action code）
 *
 * 把后端 raw action code（ban_user / update_category / change_novel_status_listed …）映射成：
 * - label：人性化中文动作短语（行内「做了什么」，对齐设计稿 what）
 * - type：动作类型（8 类闭集），驱动右侧 pill 色调（TYPE_TONE，单一真理源，对齐设计稿）
 *
 * 未登记 action（中间件 deriveAction 兜底 `<method>_<path>_<uuid>` / 未来新增）→ 去尾部 id 再查表，
 * 仍未命中则按动词前缀推断 type + 给通用中文动词；对象名由后端 target_name 承载（行内 target 段）。
 */

import { NOVEL_VISIBILITIES } from '@novel-admin/shared';
import { INHERIT_LABEL, visibilityLabel } from '~/lib/novel-visibility-display';

/** 动作类型闭集（对齐设计稿 TYPE filter 8 项）。 */
export type AuditActionType =
  | 'create'
  | 'update'
  | 'publish'
  | 'remove'
  | 'reply'
  | 'export'
  | 'ban'
  | 'login';

/** 类型筛选下拉选项（顺序对齐设计稿）。value=type，label=type（英文 type code，1:1 设计稿）。 */
export const AUDIT_TYPE_OPTIONS: { value: AuditActionType; label: string }[] = [
  { value: 'create', label: 'create' },
  { value: 'update', label: 'update' },
  { value: 'publish', label: 'publish' },
  { value: 'remove', label: 'remove' },
  { value: 'reply', label: 'reply' },
  { value: 'export', label: 'export' },
  { value: 'ban', label: 'ban' },
  { value: 'login', label: 'login' },
];

/** type → tone（设计稿 TYPE_TONE 单一真理源）：create/publish=绿、update/reply/export/login=蓝、remove=黄、ban=红。 */
const TYPE_TONE: Record<AuditActionType, 'pos' | 'info' | 'warn' | 'neg'> = {
  create: 'pos',
  publish: 'pos',
  update: 'info',
  reply: 'info',
  export: 'info',
  login: 'info',
  remove: 'warn',
  ban: 'neg',
};

/** type → chip 类名（未命中回退 chip-info，同设计稿 toneChip）。 */
export function auditTypeChip(type: AuditActionType): string {
  const t = TYPE_TONE[type] ?? 'info';
  return t === 'pos'
    ? 'chip-pos'
    : t === 'neg'
      ? 'chip-neg'
      : t === 'warn'
        ? 'chip-warn'
        : 'chip-info';
}

type ActionMeta = { label: string; type: AuditActionType };

/**
 * 三层可见性写的 action code（server `book-visibility-admin-service`）= `<写入口>_<档位小写>`，
 * 层② 传 `null`（清除本层覆盖）记 `_inherit`。
 *
 * **按 shared 闭集生成，不手写这十几条**：手写清单在加档位时必漏，而漏掉的那条会落到
 * `fallbackVerb` —— `set_` 前缀兜出泛化的「更新」+ 蓝色 update pill，于是日志里最该被看见的
 * 「下架」和普通改档长得一模一样，按 type 也筛不出来。中文档位名取
 * `~/lib/novel-visibility-display`（界面唯一口径），本文件不再写第二份。
 */
const VISIBILITY_WRITE_SCOPE: Record<string, { scope: string; inheritable: boolean }> = {
  set_group_default_visibility: { scope: '书组默认受众档', inheritable: false },
  set_novel_visibility: { scope: '本语言版受众档', inheritable: true },
  set_app_override: { scope: '本 App 受众档', inheritable: false },
};

/** 档位后缀 → 中文 + 动作类型。`delisted` 归 remove（破坏性），`public` 归 publish。 */
const VISIBILITY_SUFFIX_META: Record<string, { label: string; type: AuditActionType }> = {
  ...Object.fromEntries(
    NOVEL_VISIBILITIES.map((v) => [
      v.toLowerCase(),
      {
        label: visibilityLabel(v),
        type: (v === 'DELISTED'
          ? 'remove'
          : v === 'PUBLIC'
            ? 'publish'
            : 'update') as AuditActionType,
      },
    ]),
  ),
  inherit: { label: INHERIT_LABEL, type: 'update' },
};

const VISIBILITY_ACTION_META: Record<string, ActionMeta> = Object.fromEntries(
  Object.entries(VISIBILITY_WRITE_SCOPE).flatMap(([code, { scope, inheritable }]) =>
    Object.entries(VISIBILITY_SUFFIX_META)
      .filter(([suffix]) => suffix !== 'inherit' || inheritable)
      .map(([suffix, meta]) => [
        `${code}_${suffix}`,
        { label: `${scope}改为${meta.label}`, type: meta.type },
      ]),
  ),
);

/** raw action code → { 中文动作短语, 类型 }。覆盖全仓审计写入点的全量 action（含动态 status 变体 + 三元分叉）。 */
const ACTION_META: Record<string, ActionMeta> = {
  declare_usd_coverage: { label: '声明 USD 来源覆盖', type: 'update' },
  verify_usd_coverage: { label: '核验 USD 来源覆盖', type: 'update' },
  revoke_usd_coverage: { label: '撤销 USD 来源覆盖', type: 'remove' },
  // App
  create_app: { label: '新建 App', type: 'create' },
  update_app: { label: '更新 App 配置', type: 'update' },
  copy_app: { label: '复制 App', type: 'create' },
  upsert_rewarded_ad_language_config: { label: '保存激励广告语言配置', type: 'update' },
  delete_rewarded_ad_language_config: { label: '删除激励广告语言配置', type: 'remove' },
  upsert_rewarded_ad_unit: { label: '登记激励广告单元版本', type: 'update' },
  activate_app: { label: '启用 App', type: 'update' },
  deactivate_app: { label: '停用 App', type: 'update' },
  enable_payment_method: { label: '启用支付方式', type: 'update' },
  disable_payment_method: { label: '停用支付方式', type: 'update' },
  // 书籍
  create_novel: { label: '新建书籍', type: 'create' },
  duplicate_novel: { label: '复制书籍', type: 'create' },
  update_novel: { label: '编辑书籍', type: 'update' },
  update_novel_metadata: { label: '书籍信息更新', type: 'update' },
  update_novel_chapters: { label: '连载更新', type: 'update' },
  // dd#970：书组级字段（中文识别名 / 原作信息 / 权属 / 展示基数）改走 PATCH /book-groups/:id
  update_book_group: { label: '编辑书组信息', type: 'update' },
  create_novel_language: { label: '新增书籍语言版本', type: 'create' },
  upload_book_cover: { label: '上传书籍封面', type: 'update' },
  // 四档三层写（dd#941）：按 shared 闭集生成，见 VISIBILITY_ACTION_META
  ...VISIBILITY_ACTION_META,
  remove_app_override: { label: '移除 App 可见性覆盖', type: 'remove' },
  // 三态时代的 action code —— **存量审计行仍是这些值**（日志只读、不回填），故保留登记；
  // 新写入不再产生它们（`set_app_override_delisted` 在四档下由上面那份生成，同名同义）。
  change_novel_status_listed: { label: '上架书籍', type: 'publish' },
  change_novel_status_delisted: { label: '下架书籍', type: 'remove' },
  change_novel_status_hidden: { label: '隐藏书籍', type: 'remove' },
  set_app_override_listed: { label: '设为本 App 上架', type: 'update' },
  set_app_override_hidden: { label: '设为本 App 隐藏', type: 'update' },
  // 章节
  create_chapter: { label: '新建章节', type: 'create' },
  update_chapter: { label: '编辑章节', type: 'update' },
  publish_chapter: { label: '发布章节', type: 'publish' },
  enqueue_publish: { label: '加入发布队列', type: 'publish' },
  promote_chapter_draft: { label: '重新发布章节草稿', type: 'publish' },
  rebase_chapter_draft: { label: '重建章节草稿基线', type: 'update' },
  configure_publish_schedule: { label: '配置发布计划', type: 'update' },
  cancel_publish_queue: { label: '取消发布队列', type: 'remove' },
  // 分类 / 标签
  create_category: { label: '新建分类', type: 'create' },
  update_category: { label: '编辑分类', type: 'update' },
  delete_category: { label: '删除分类', type: 'remove' },
  create_tag: { label: '新建标签', type: 'create' },
  update_tag: { label: '编辑标签', type: 'update' },
  delete_tag: { label: '删除标签', type: 'remove' },
  merge_tags: { label: '合并标签', type: 'update' },
  purge_tag: { label: '清除标签', type: 'remove' },
  // Banner / 运营位
  create_banner: { label: '新建 Banner', type: 'create' },
  update_banner: { label: '编辑 Banner', type: 'update' },
  delete_banner: { label: '删除 Banner', type: 'remove' },
  create_banner_slide: { label: '新建 Banner 轮播图', type: 'create' },
  update_banner_slide: { label: '编辑 Banner 轮播图', type: 'update' },
  delete_banner_slide: { label: '删除 Banner 轮播图', type: 'remove' },
  upload_banner_slide_image: { label: '上传轮播图图片', type: 'update' },
  create_shelf_recommendation: { label: '新建书架推荐', type: 'create' },
  update_shelf_recommendation: { label: '编辑书架推荐', type: 'update' },
  delete_shelf_recommendation: { label: '删除书架推荐', type: 'remove' },
  create_home_section: { label: '新建首页栏目', type: 'create' },
  update_home_section: { label: '编辑首页栏目', type: 'update' },
  delete_home_section: { label: '删除首页栏目', type: 'remove' },
  create_operational_popup_scene: { label: '新建运营弹窗', type: 'create' },
  update_operational_popup_scene: { label: '更新运营弹窗', type: 'update' },
  delete_operational_popup_scene: { label: '删除运营弹窗', type: 'remove' },
  // 支付 / 商品
  create_payment_offer: { label: '新建支付优惠', type: 'create' },
  update_payment_offer: { label: '编辑支付优惠', type: 'update' },
  activate_payment_offer: { label: '启用支付优惠', type: 'update' },
  deactivate_payment_offer: { label: '停用支付优惠', type: 'update' },
  delete_payment_offer: { label: '删除支付优惠', type: 'remove' },
  // 通道策略（ADM-PAY-S008）：replace_* 无动词前缀规则可兜，不登记会落到泛化的「操作」
  replace_payment_method_policy: { label: '配置支付通道策略', type: 'update' },
  delete_payment_method_policy: { label: '删除通道地区覆盖', type: 'remove' },
  // 通道图标设置（ADM-APP-S013 / A8 §7.8.5~§7.8.7）。不登记的话：`update_` 前缀只兜出泛化的
  // 「更新」；`upload_` **不在任何前缀规则里**，会一路落到最后的兜底「操作」。
  update_payment_method_setting: { label: '配置支付方式图标与底色', type: 'update' },
  upload_payment_method_icon: { label: '上传支付通道图标', type: 'update' },
  create_payment_page: { label: '新建支付页', type: 'create' },
  update_payment_page: { label: '编辑支付页', type: 'update' },
  delete_payment_page: { label: '删除支付页', type: 'remove' },
  create_paywall: { label: '新建付费墙', type: 'create' },
  update_paywall: { label: '更新付费墙', type: 'update' },
  create_product_metadata: { label: '新建商品', type: 'create' },
  update_product_metadata: { label: '编辑商品', type: 'update' },
  revive_product_metadata: { label: '恢复商品', type: 'update' },
  activate_product_metadata: { label: '启用商品', type: 'update' },
  deactivate_product_metadata: { label: '停用商品', type: 'update' },
  delete_product_metadata: { label: '删除商品', type: 'remove' },
  // 推送
  create_push_task: { label: '创建推送任务', type: 'create' },
  cancel_push_task: { label: '取消推送任务', type: 'remove' },
  update_push_template: { label: '编辑推送模板', type: 'update' },
  push_template_test_send: { label: '推送模板测试发送', type: 'publish' },
  // 反馈 / FAQ
  reply_feedback: { label: '回复反馈工单', type: 'reply' },
  add_feedback_internal_note: { label: '添加反馈内部备注', type: 'reply' },
  close_feedback: { label: '关闭反馈工单', type: 'remove' },
  create_faq: { label: '新建 FAQ', type: 'create' },
  update_faq: { label: '编辑 FAQ', type: 'update' },
  // 用户 / 钱包 / 财务
  ban_user: { label: '封禁用户', type: 'ban' },
  risk_control_auto_ban_device: { label: '风控自动封禁设备', type: 'ban' },
  risk_control_auto_ban_user: { label: '风控自动封禁账号', type: 'ban' },
  risk_control_observe_user: { label: '风控自动观察账号', type: 'update' },
  unban_user: { label: '解封用户', type: 'update' },
  update_user_locale: { label: '修改用户地区/语言', type: 'update' },
  grant_bonus: { label: '发放 BONUS', type: 'update' },
  add_device_whitelist: { label: '添加设备白名单', type: 'create' },
  remove_device_whitelist: { label: '移除设备白名单', type: 'remove' },
  release_device_sanction: { label: '解除设备封禁', type: 'update' },
  update_refund_note: { label: '更新退款备注', type: 'update' },
  dashboard_export: { label: '导出看板数据', type: 'export' },
  // 风控 / 系统配置
  set_risk_rule_enabled: { label: '启停风控规则', type: 'update' },
  set_risk_rule_threshold: { label: '调整风控阈值', type: 'update' },
  set_feedback_translation_enabled: { label: '开关反馈翻译', type: 'update' },
  update_system_language_enabled: { label: '开关系统语言', type: 'update' },
  update_system_region_enabled: { label: '开关系统地区', type: 'update' },
  create_ad_plan_web_link: { label: '新建 Web 投放链接', type: 'create' },
  update_ad_plan_web_link: { label: '编辑 Web 投放链接', type: 'update' },
  delete_ad_plan_web_link: { label: '删除 Web 投放链接', type: 'remove' },
  create_landing_product: { label: '新建投放产品', type: 'create' },
  update_landing_product: { label: '编辑投放产品', type: 'update' },
  create_landing_page: { label: '新建落地页任务', type: 'create' },
  update_landing_page: { label: '编辑落地页任务', type: 'update' },
  regenerate_landing_page: { label: '重新生成落地页', type: 'update' },
  publish_landing_page: { label: '发布落地页', type: 'update' },
  put_landing_copy: { label: '保存落地页文案', type: 'update' },
  delete_landing_copy: { label: '恢复落地页默认文案', type: 'remove' },
  // 运营账号 / 自助 / 鉴权
  update_admin_user: { label: '编辑运营账号', type: 'update' },
  delete_admin_user: { label: '删除运营账号', type: 'remove' },
  resend_invite: { label: '重发邀请邮件', type: 'update' },
  invite_admin: { label: '邀请运营账号', type: 'create' },
  bind_totp: { label: '绑定 TOTP', type: 'update' },
  totp_setup_retrieve: { label: '拉取 TOTP 配置', type: 'update' },
  start_totp_rebind: { label: '发起 TOTP 重绑', type: 'update' },
  rebind_totp: { label: '完成 TOTP 重绑', type: 'update' },
  regenerate_backup_codes: { label: '重新生成备份码', type: 'update' },
  change_own_password: { label: '修改密码', type: 'update' },
  update_own_profile: { label: '更新个人资料', type: 'update' },
  update_own_avatar: { label: '更新头像', type: 'update' },
  update_own_notification_prefs: { label: '更新通知偏好', type: 'update' },
  revoke_own_session: { label: '撤销登录会话', type: 'remove' },
  revoke_trusted_device: { label: '撤销受信设备', type: 'remove' },
  logout: { label: '退出登录', type: 'login' },
  logout_all_devices: { label: '退出全部设备', type: 'login' },
  login_mfa_bypass: { label: '免二次验证登录（dev 白名单）', type: 'login' },
};

/** 去掉中间件 deriveAction 兜底拼在尾部的对象 id（uuid，含/不含连字符），还原成可查的 base action。 */
function stripTrailingId(action: string): string {
  return action
    .replace(/_[0-9a-f]{8}(-?[0-9a-f]{4}){3}-?[0-9a-f]{12}$/i, '')
    .replace(/_[0-9a-f]{24,}$/i, '');
}

/** 兜底：按动词前缀推断中文动词 + type（对象名由 target_name 承载，故只给动词）。 */
function fallbackVerb(action: string): ActionMeta {
  const a = action.toLowerCase();
  if (/^(create|add|invite|post)_/.test(a)) return { label: '创建', type: 'create' };
  if (/^(delete|remove|purge|cancel|revoke|close)_/.test(a))
    return { label: '删除', type: 'remove' };
  if (/^publish_/.test(a)) return { label: '发布', type: 'publish' };
  if (/^reply_/.test(a)) return { label: '回复', type: 'reply' };
  if (a.includes('export')) return { label: '导出', type: 'export' };
  if (/^ban_/.test(a)) return { label: '封禁', type: 'ban' };
  if (/^(logout|login)/.test(a)) return { label: '登录会话', type: 'login' };
  if (
    /^(update|set|change|patch|put|rebind|regenerate|enable|disable|activate|deactivate)_?/.test(a)
  )
    return { label: '更新', type: 'update' };
  if (/^get_/.test(a) || a.includes('retrieve')) return { label: '查询', type: 'export' };
  return { label: '操作', type: 'update' };
}

/** raw action code → 展示用 { 中文动作短语, 类型 }。命中映射表优先，再去尾 id 重查，最后动词兜底。 */
export function humanizeAuditAction(action: string): ActionMeta {
  return ACTION_META[action] ?? ACTION_META[stripTrailingId(action)] ?? fallbackVerb(action);
}
